import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { MapService } from '../services/map.service';

import { LocationFormatter } from '../utils/location-formatter';

import { loadModules } from 'esri-loader';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser
declare let proj4: any;       // proj4js-2.3.14.js, http://proj4js.org/
declare let mgrs: any;        // mgrs.js 


// Non-namespaced global functions from usng_modified.js
// See: usng.js (U.S. National Grid functions)
// Module to calculate National Grid Coordinates	
declare function LLtoMGRS(lat: any, lon: any, precision: any, _mgrs_name?: any): any;
declare function USNGtoLL(x: any, latlong: any): any;



@Injectable({
	providedIn: 'root'
})
export class GeoService {

	private pi = 3.14159265358979;

	/* Ellipsoid model constants (actual values here are for WGS84) */
	private sm_a = 6378137.0;
	private sm_b = 6356752.314;
	private sm_EccSquared = 6.69437999013e-03;

	private UTMScaleFactor = 0.9996;

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private mapService: MapService
	) { }

	DegToRad(deg: number): number {
		return (deg / 180.0 * this.pi);
	}

	RadToDeg(rad: number): number {
		return (rad / this.pi * 180.0)
	}

	/*
	* Determines the central meridian for the given UTM zone.
	* Inputs:
	*     zone - An integer value designating the UTM zone, range [1,60].
	* Returns:
	*   The central meridian for the given UTM zone, in radians, or zero
	*   if the UTM zone parameter is outside the range [1,60].
	*   Range of the central meridian is the radian equivalent of [-177,+177].
	*/
	UTMCentralMeridian(zone: number): number {
		return this.DegToRad(-183.0 + (zone * 6.0));
	}

	/*
	* Computes the ellipsoidal distance from the equator to a point at a
	* given latitude.
	* Inputs:
	*     phi - Latitude of the point, in radians.
	* Returns:
	*     The ellipsoidal distance of the point from the equator, in meters.
	*/
	ArcLengthOfMeridian(phi: number): number {
		let n = (this.sm_a - this.sm_b) / (this.sm_a + this.sm_b);

		let alpha = ((this.sm_a + this.sm_b) / 2.0) * (1.0 + (Math.pow(n, 2.0) / 4.0) + (Math.pow(n, 4.0) / 64.0));

		let beta = (-3.0 * n / 2.0) + (9.0 * Math.pow(n, 3.0) / 16.0) + (-3.0 * Math.pow(n, 5.0) / 32.0);

		let gamma = (15.0 * Math.pow(n, 2.0) / 16.0) + (-15.0 * Math.pow(n, 4.0) / 32.0);

		let delta = (-35.0 * Math.pow(n, 3.0) / 48.0) + (105.0 * Math.pow(n, 5.0) / 256.0);

		let epsilon = (315.0 * Math.pow(n, 4.0) / 512.0);

		return alpha * (phi + (beta * Math.sin(2.0 * phi)) + (gamma * Math.sin(4.0 * phi)) + (delta * Math.sin(6.0 * phi)) + (epsilon * Math.sin(8.0 * phi)));
	}

	/*
	* Converts a latitude/longitude pair to x and y coordinates in the
	* Universal Transverse Mercator projection.
	* Inputs:
	*   lat - Latitude of the point, in radians.
	*   lon - Longitude of the point, in radians.
	*   zone - UTM zone to be used for calculating values for x and y.
	*          If zone is less than 1 or greater than 60, the routine
	*          will determine the appropriate zone from the value of lon.
	* Outputs:
	*   xy - A 2-element array where the UTM x and y values will be stored.
	* Returns:
	*   The UTM zone used for calculating the values of x and y.
	*/
	LatLonToUTMXY(lat: number, lon: number, zone: number, xy: any): number {
		this.MapLatLonToXY(lat, lon, this.UTMCentralMeridian(zone), xy);

		xy[0] = xy[0] * this.UTMScaleFactor + 500000.0;
		xy[1] = xy[1] * this.UTMScaleFactor;
		if (xy[1] < 0.0) {
			xy[1] = xy[1] + 10000000.0;
		}

		return zone;
	}

	/*
	* Computes the footpoint latitude for use in converting transverse
	* Mercator coordinates to ellipsoidal coordinates.
	* Inputs:
	*   y - The UTM northing coordinate, in meters.
	* Returns:
	*   The footpoint latitude, in radians.
	*/
	FootpointLatitude(y: number): number {
		let n = (this.sm_a - this.sm_b) / (this.sm_a + this.sm_b);

		let alpha_ = ((this.sm_a + this.sm_b) / 2.0) * (1 + (Math.pow(n, 2.0) / 4) + (Math.pow(n, 4.0) / 64));

		let y_ = y / alpha_;

		let beta_ = (3.0 * n / 2.0) + (-27.0 * Math.pow(n, 3.0) / 32.0) + (269.0 * Math.pow(n, 5.0) / 512.0);

		let gamma_ = (21.0 * Math.pow(n, 2.0) / 16.0) + (-55.0 * Math.pow(n, 4.0) / 32.0);

		let delta_ = (151.0 * Math.pow(n, 3.0) / 96.0) + (-417.0 * Math.pow(n, 5.0) / 128.0);

		let epsilon_ = (1097.0 * Math.pow(n, 4.0) / 512.0);

		let result = y_ + (beta_ * Math.sin(2.0 * y_)) + (gamma_ * Math.sin(4.0 * y_)) + (delta_ * Math.sin(6.0 * y_)) + (epsilon_ * Math.sin(8.0 * y_));
		return result;
	}

	/*
	* Converts a latitude/longitude pair to x and y coordinates in the
	* Transverse Mercator projection.  Note that Transverse Mercator is not
	* the same as UTM; a scale factor is required to convert between them.
	* Inputs:
	*    phi - Latitude of the point, in radians.
	*    lambda - Longitude of the point, in radians.
	*    lambda0 - Longitude of the central meridian to be used, in radians.
	* Outputs:
	*    xy - A 2-element array containing the x and y coordinates
	*         of the computed point.
	* Returns:
	*    The function does not return a value.
	*/
	MapLatLonToXY(phi: number, lambda: number, lambda0: number, xy: any[]): void {
		let N, nu2, ep2, t, t2, l;
		let l3coef, l4coef, l5coef, l6coef, l7coef, l8coef;
		let tmp;

		ep2 = (Math.pow(this.sm_a, 2.0) - Math.pow(this.sm_b, 2.0)) / Math.pow(this.sm_b, 2.0);

		nu2 = ep2 * Math.pow(Math.cos(phi), 2.0);

		N = Math.pow(this.sm_a, 2.0) / (this.sm_b * Math.sqrt(1 + nu2));

		t = Math.tan(phi);
		t2 = t * t;
		tmp = (t2 * t2 * t2) - Math.pow(t, 6.0);

		l = lambda - lambda0;

		l3coef = 1.0 - t2 + nu2;

		l4coef = 5.0 - t2 + 9 * nu2 + 4.0 * (nu2 * nu2);

		l5coef = 5.0 - 18.0 * t2 + (t2 * t2) + 14.0 * nu2 - 58.0 * t2 * nu2;

		l6coef = 61.0 - 58.0 * t2 + (t2 * t2) + 270.0 * nu2 - 330.0 * t2 * nu2;

		l7coef = 61.0 - 479.0 * t2 + 179.0 * (t2 * t2) - (t2 * t2 * t2);

		l8coef = 1385.0 - 3111.0 * t2 + 543.0 * (t2 * t2) - (t2 * t2 * t2);

		xy[0] = N * Math.cos(phi) * l + (N / 6.0 * Math.pow(Math.cos(phi), 3.0) * l3coef * Math.pow(l, 3.0)) + (N / 120.0 * Math.pow(Math.cos(phi), 5.0) * l5coef * Math.pow(l, 5.0)) + (N / 5040.0 * Math.pow(Math.cos(phi), 7.0) * l7coef * Math.pow(l, 7.0));

		xy[1] = this.ArcLengthOfMeridian(phi) + (t / 2.0 * N * Math.pow(Math.cos(phi), 2.0) * Math.pow(l, 2.0)) + (t / 24.0 * N * Math.pow(Math.cos(phi), 4.0) * l4coef * Math.pow(l, 4.0)) + (t / 720.0 * N * Math.pow(Math.cos(phi), 6.0) * l6coef * Math.pow(l, 6.0)) + (t / 40320.0 * N * Math.pow(Math.cos(phi), 8.0) * l8coef * Math.pow(l, 8.0));

		return;
	}

	/*
	* Converts x and y coordinates in the Transverse Mercator projection to
	* a latitude/longitude pair.  Note that Transverse Mercator is not
	* the same as UTM; a scale factor is required to convert between them.
	* Inputs:
	*   x - The easting of the point, in meters.
	*   y - The northing of the point, in meters.
	*   lambda0 - Longitude of the central meridian to be used, in radians.
	* Outputs:
	*   philambda - A 2-element containing the latitude and longitude
	*               in radians.
	* Returns:
	*   The function does not return a value.
	*/
	MapXYToLatLon(x: number, y: number, lambda0: number, philambda: number[]): void {
		let phif, Nf, Nfpow, nuf2, ep2, tf, tf2, tf4, cf
		let x1frac, x2frac, x3frac, x4frac, x5frac, x6frac, x7frac, x8frac;
		let x2poly, x3poly, x4poly, x5poly, x6poly, x7poly, x8poly;

		phif = this.FootpointLatitude(y)

		ep2 = (Math.pow(this.sm_a, 2.0) - Math.pow(this.sm_b, 2.0)) / Math.pow(this.sm_b, 2.0);

		/* Precalculate cos (phif) */
		cf = Math.cos(phif)

		nuf2 = ep2 * Math.pow(cf, 2.0);

		Nf = Math.pow(this.sm_a, 2.0) / (this.sm_b * Math.sqrt(1 + nuf2));
		Nfpow = Nf;

		tf = Math.tan(phif)
		tf2 = tf * tf;
		tf4 = tf2 * tf2;

		x1frac = 1.0 / (Nfpow * cf);

		Nfpow *= Nf; /* now equals Nf**2) */;
		x2frac = tf / (2.0 * Nfpow);

		Nfpow *= Nf; /* now equals Nf**3) */;
		x3frac = 1.0 / (6.0 * Nfpow * cf);

		Nfpow *= Nf; /* now equals Nf**4) */;
		x4frac = tf / (24.0 * Nfpow);

		Nfpow *= Nf; /* now equals Nf**5) */;
		x5frac = 1.0 / (120.0 * Nfpow * cf);

		Nfpow *= Nf; /* now equals Nf**6) */;
		x6frac = tf / (720.0 * Nfpow);

		Nfpow *= Nf; /* now equals Nf**7) */;
		x7frac = 1.0 / (5040.0 * Nfpow * cf);

		Nfpow *= Nf; /* now equals Nf**8) */;
		x8frac = tf / (40320.0 * Nfpow);

		x2poly = -1.0 - nuf2;

		x3poly = -1.0 - 2 * tf2 - nuf2;

		x4poly = 5.0 + 3.0 * tf2 + 6.0 * nuf2 - 6.0 * tf2 * nuf2 - 3.0 * (nuf2 * nuf2) - 9.0 * tf2 * (nuf2 * nuf2);

		x5poly = 5.0 + 28.0 * tf2 + 24.0 * tf4 + 6.0 * nuf2 + 8.0 * tf2 * nuf2;

		x6poly = -61.0 - 90.0 * tf2 - 45.0 * tf4 - 107.0 * nuf2 + 162.0 * tf2 * nuf2;

		x7poly = -61.0 - 662.0 * tf2 - 1320.0 * tf4 - 720.0 * (tf4 * tf2);

		x8poly = 1385.0 + 3633.0 * tf2 + 4095.0 * tf4 + 1575 * (tf4 * tf2);

		philambda[0] = phif + x2frac * x2poly * (x * x) + x4frac * x4poly * Math.pow(x, 4.0) + x6frac * x6poly * Math.pow(x, 6.0) + x8frac * x8poly * Math.pow(x, 8.0)

		philambda[1] = lambda0 + x1frac * x + x3frac * x3poly * Math.pow(x, 3.0) + x5frac * x5poly * Math.pow(x, 5.0) + x7frac * x7poly * Math.pow(x, 7.0);

		return;
	}

	/*
	* Converts x and y coordinates in the Universal Transverse Mercator
	* projection to a latitude/longitude pair.
	* Inputs:
	*    x - The easting of the point, in meters.
	*    y - The northing of the point, in meters.
	*    zone - The UTM zone in which the point lies.
	*    southhemi - True if the point is in the southern hemisphere
	*               false otherwise.
	* Outputs:
	*    latlon - A 2-element array containing the latitude and
	*            longitude of the point, in radians.
	* Returns:
	*    The function does not return a value.
	*/
	UTMXYToLatLon(x: number, y: number, zone: any, southhemi: boolean, latlon: number[]): void {
		let cmeridian;

		x -= 500000.0;
		x /= this.UTMScaleFactor;

		/* If in southern hemisphere, adjust y accordingly. */
		if (southhemi)
			y -= 10000000.0;

		y /= this.UTMScaleFactor;

		cmeridian = this.UTMCentralMeridian(zone);
		this.MapXYToLatLon(x, y, cmeridian, latlon);

		return;
	}

	isNumber(n: any): boolean {
		return !isNaN(parseFloat(n)) && isFinite(n);
	}

	validTime(x: any, y: any): boolean {
		if (this.isNumber(x) && this.isNumber(y) && (x <= 59 && x >= 0) && (y <= 59 && y >= 0))
			return true;

		return false;
	}

	DDD2DDD(x: any, y: any): any {
		if (this.validDDD(x, y)) {
			return {
				x: x,
				y: y
			};
		}
		return null;
	}

	validDDD(x: any, y: any): boolean {
		if (this.isNumber(x) && this.isNumber(y) && (x <= 180 && x >= -180) && (y >= -90 && y <= 90))
			return true;

		return false;
	}

	validDMS(xD: any, xM: any, xS: any, yD: any, yM: any, yS: any): boolean {
		if (this.isNumber(xD) && this.isNumber(yD) && (xD >= 0 && xD <= 180) && (yD >= 0 && yD <= 90) && this.validTime(xM, yM) && this.validTime(xS, yS)) {
			return true;
		}

		return false;
	}

	validCCS(easting: any, northing: any, nad83: any): any {
		if (this.isNumber(northing) && this.isNumber(easting) && this.isNumber(nad83)) {
			let ll = [];
			let nad83proj = this.configService.getConfig().esri.projections[parseInt(nad83)].projection;
			let mp = {
				x: parseFloat(easting),
				y: parseFloat(northing)
			};
			mp = proj4(nad83proj).inverse(mp);
			if (this.validDDD(mp.x, mp.y)) {
				return {
					x: mp.x.toFixed(3),
					y: mp.y.toFixed(3)
				};
			}
		}
		return false;
	}

	validUTM(easting: any, northing: any, zone: any, isSouth: boolean): any {
		if (this.isNumber(northing) && this.isNumber(easting) && this.isNumber(zone) && zone <= 60 && zone >= 0) {
			let latlon: number[] = [];
			this.UTMXYToLatLon(parseFloat(easting),
				parseFloat(northing), parseFloat(zone),
				isSouth, latlon);
			let x = this.RadToDeg(latlon[1]);
			let y = this.RadToDeg(latlon[0]);
			if (this.validDDD(x, y)) {
				return {
					x: latlon[1],
					y: latlon[0]
				};
			}
		}

		return false;
	}

	// Doesn't seem to be called by KV
	validMGRS(x?: any): any {
		try {
			if (x != null) {
				let latlong: any[] = [];
				USNGtoLL(x, latlong);
				if (isNaN(latlong[0]) || isNaN(latlong[1])) {
					return false;
				}
				return latlong;
			}
		} catch (exp) {
			return false;
		}

		return false;
	}

	UTM2DDD(northing: any, easting: any, zone: any, isSouth: boolean): any {
		let latlon = this.validUTM(easting, northing, zone, isSouth);
		if (latlon)
			return {
				x: this.RadToDeg(latlon.x),
				y: this.RadToDeg(latlon.y)
			};

		return null;
	}

	CCS2DDD(northing: any, easting: any, nad83: any): any {
		let latlon = this.validCCS(easting, northing, nad83);
		if (latlon) {
			return latlon;
		}
		return null;
	}

	DDD2CCS(x: any, y: any, nad83: any): any {
		if (this.validDDD(x, y)) {
			let nad83proj = this.configService.getConfig().esri.projections[parseInt(nad83)].projection;
			let mp = {
				x: x,
				y: y
			};
			mp = proj4(nad83proj).forward(mp);
			return {
				x: mp.x.toFixed(3),
				y: mp.y.toFixed(3)
			};
		}
		return null;
	}

	DDD2DMS(x: any, y: any): any {
		if (this.validDDD(x, y)) {
			var xDMS = LocationFormatter.decimalLongToDMS(x);
			var yDMS = LocationFormatter.decimalLatToDMS(y);
			return [xDMS, yDMS];
		}
		return null;
	}

	DDD2UTM(x: any, y: any): any {
		if (this.validDDD(x, y)) {
			let xy: any[] = [];
			let zone = Math.floor((x + 180.0) / 6) + 1;
			zone = this.LatLonToUTMXY(this.DegToRad(y), this.DegToRad(x), zone, xy);

			return {
				northing: xy[1],
				easting: xy[0],
				zone: zone,
				orientation: y < 0 ? "S" : "N"
			};
		}
		return null;
	}

	DDD2MGRS(x: any, y: any): any {
		if (this.validDDD(x, y)) {
			return LLtoMGRS(y, x, 5);
		}
		return null;
	}

	MGRS2DDD(x: any): any {
		return this.validMGRS(x);
	}

	DMS2DDD(xD: any, xM: any, xS: any, yD: any, yM: any, yS: any, longitudeD: any, latitudeD: any): any {
		if (this.validDMS(xD, xM, xS, yD, yM, yS)) {
			let x = LocationFormatter
				.DMSToDecimal(
					parseFloat(xD),
					parseFloat(xM),
					parseFloat(xS),
					parseFloat(longitudeD) > 0 ? LocationFormatter.EAST : LocationFormatter.WEST)
			let y = LocationFormatter
				.DMSToDecimal(
					parseFloat(yD),
					parseFloat(yM),
					parseFloat(yS),
					parseFloat(latitudeD) > 0 ? LocationFormatter.NORTH : LocationFormatter.SOUTH)

			return {
				x: x,
				y: y
			};
		}
		return null;
	}

	isEmpty(val: any): boolean {
		let isEmpty = true;

		val.forEach((curr: any) => {
			if (curr != null && curr != "")
				isEmpty = false;
		});

		return isEmpty;
	}

	numerize(xy: any): any {
		if (xy == null)
			return xy;
		if (xy.x != null)
			xy.x = xy.x * 1;

		if (xy.y != null)
			xy.y = xy.y * 1;

		return xy;
	}

	async parseWKT(wktString: string): Promise<any> {
		const [Polygon, Point, Circle, Polyline, Multipoint, SpatialReference, webMercatorUtils] =
			await loadModules([
				'esri/geometry/Polygon',
				'esri/geometry/Point',
				'esri/geometry/Circle',
				'esri/geometry/Polyline',
				'esri/geometry/Multipoint',
				'esri/geometry/SpatialReference',
				'esri/geometry/support/webMercatorUtils'
			]);


		let geom;
		if (wktString.indexOf("CIRCLE") == 0) {
			let beg = wktString.indexOf("(");
			let end = wktString.indexOf(")");
			let sub = wktString.substring(beg + 1, end);
			let comp = sub.split(/[\s,]+/);
			let y = parseFloat(comp[0]);
			let x = parseFloat(comp[1]);
			let d = parseFloat(comp[2].replace("d=", "")) * 1000 * 111.32;
			let point = new Point([x, y]);
			geom = new Circle(point, {
				"radius": d,
				geodesic: true
			});

			for (let c = 0; c < geom.rings[0].length; c++) {
				geom.rings[0][c] = webMercatorUtils.lngLatToXY(geom.rings[0][c][0], geom.rings[0][c][1]);
			}
			geom.spatialReference.wkid = 102100;
			return geom;
		} else {
			let primitive = undefined;
			try {
				let mpgeo = this.parseMultipointGeometry(wktString);
				primitive = Terraformer.WKT.parse(mpgeo);
			} catch (err) {
				console.error("Failed to parse geometry and determine type: ", err);
				return null;
			}
			let rawGeom = Terraformer.ArcGIS.convert(primitive);
			if (primitive instanceof Terraformer.LineString || primitive instanceof Terraformer.MultiLineString) {
				geom = new Polyline(rawGeom.paths);
			} else if (primitive instanceof Terraformer.Point) {
				geom = new Point(rawGeom);
			} else if (primitive instanceof Terraformer.MultiPoint) {
				try {
					geom = new Multipoint(new SpatialReference({ wkid: 4326 }))
					geom.points = rawGeom.points;
				} catch (err) {
					console.error("Failed to parse geometry: ", err);
					return null;
				}
			} else if (primitive instanceof Terraformer.Polygon || primitive instanceof Terraformer.MultiPolygon) {
				geom = new Polygon(rawGeom.rings);
			} else {
				//fallback on Point;
				geom = new Point(rawGeom.rings);
			}
			return geom;
		}
	}

	/* Parse POINTs */
	parseMultipointGeometry(geo: string): string | null {
		try {
			if (geo && geo.indexOf("POINT") > -1) {
				let multi = geo.split(";");
				if (multi.length > 1) {
					let points = [];
					for (let i = 0; i < multi.length; i++) {
						points.push(multi[i].replace("POINT", ""));
					}
					geo = "MULTIPOINT(" + points.join(",") + ")";
				}
			};
		} catch (err) {
			return null;
		}
		return geo;
	}

	async processManualEntry(aoi: any, locate: any): Promise<any> {
		let coordinate1 = { system: aoi.mode, ccsnad83: aoi.ccsnad83 || '', x: 0, y: 0 };
		let coordinate2 = { system: aoi.mode, ccsnad83: aoi.ccsnad83 || '', x: 0, y: 0 };

		if (aoi.shape === "rectangle") {
			if (aoi.mode === "DDD") {
				Object.assign(coordinate1, aoi.ddd1);
				Object.assign(coordinate2, aoi.ddd2);
			}
			else if (aoi.mode === "DMS") {
				Object.assign(coordinate1, aoi.dms1);
				Object.assign(coordinate2, aoi.dms2);
			}
			else if (aoi.mode === "UTM") {
				Object.assign(coordinate1, aoi.utm1);
				Object.assign(coordinate2, aoi.utm2);
			}
			else if (aoi.mode === "MGRS") {
				Object.assign(coordinate1, aoi.mgrs1);
				Object.assign(coordinate2, aoi.mgrs2);
			}

			let answer1 = this.locateCoordinate(coordinate1);
			let answer2 = this.locateCoordinate(coordinate2);

			if (answer1.error) {
				this.alertsService.error({
					title: "Input Error",
					message: answer1.error,
					delay: 5000
				});
				return;
			}
			else if (answer2.error) {
				this.alertsService.error({
					title: "Input Error",
					message: answer2.error,
					delay: 5000
				});
				return;
			}
			else if (answer1.x * 1 > answer2.x * 1 || answer1.y * 1 < answer2.y * 1) {
				this.alertsService.error({
					title: "Input Error",
					message: "Upper Left must be to the Northwest of Bottom Right",
					delay: 5000
				});
				return;
			}

			const [
				Polygon,
				SpatialReference,
				webMercatorUtils
			] = await loadModules([
				"esri/geometry/Polygon",
				"esri/geometry/SpatialReference",
				"esri/geometry/support/webMercatorUtils"
			]);

			let polygon = new Polygon(new SpatialReference({ wkid: 4326 }));
			let xmin = coordinate1.x;
			let xmax = coordinate2.x;
			let ymin = coordinate2.y;
			let ymax = coordinate1.y;
			polygon.addRing([[xmin, ymax], [xmax, ymax], [xmax, ymin], [xmin, ymin], [xmin, ymax]]);
			polygon = webMercatorUtils.geographicToWebMercator(polygon);
			if (locate) {
				this.mapService.broadcastAddLocationZoomEvent({ geometry: polygon, search: false });
				return;
			}
			else {
				this.mapService.broadcastAddAOIEvent({ geometry: polygon, forceZoom: true });
				return { point1: coordinate1, point2: coordinate2 };
			}
		}
		else {
			if (aoi.mode === "DDD") {
				Object.assign(coordinate1, aoi.ddd1);
			}
			else if (aoi.mode === "DMS") {
				Object.assign(coordinate1, aoi.dms1);
			}
			else if (aoi.mode === "UTM") {
				Object.assign(coordinate1, aoi.utm1);
			}
			else if (aoi.mode === "MGRS") {
				Object.assign(coordinate1, aoi.mgrs1);
			}

			let answer1 = this.locateCoordinate(coordinate1);

			if (aoi.radius == null || !this.isNumber(aoi.radius) || aoi.radius <= 0) {
				this.alertsService.error({
					title: "Input Error",
					message: "A number greater than 0 required for Radius",
					delay: 5000
				});
				return;
			}

			if (answer1.error) {
				this.alertsService.error({
					title: "Input Error",
					message: answer1.error,
					delay: 5000
				});
				return;
			}

			const [
				SpatialReference,
				Point,
				Circle,
				projection
			] = await loadModules([
				"esri/geometry/SpatialReference",
				"esri/geometry/Point",
				"esri/geometry/Circle",
				"esri/geometry/projection"
			]);

			// Define point in WGS84
			let center = new Point({
				longitude: coordinate1.x, // degrees
				latitude: coordinate1.y,  // degrees
				spatialReference: { wkid: 4326 }
			});

			// Create circle in WGS84
			let circle = new Circle({
				center: center,
				radius: aoi.radius,
				radiusUnit: aoi.radiusType,
				geodesic: true
			});

			// Project circle to Web Mercator
			projection.load().then(() => {
				let circleWM = projection.project(circle, new SpatialReference({ wkid: 102100 }));
				circleWM._srsShape = "circle";

				if (locate) {
					this.mapService.broadcastAddLocationZoomEvent({ geometry: circleWM, search: false });
					return;
				}
				else {
					this.mapService.broadcastAddAOIEvent({ geometry: circleWM, forceZoom: true });
					return { point1: coordinate1 };
				}
			});
		}
	}

	locateCoordinate(coordinate: any): any {
		let system = coordinate.system;
		let latlon = null;
		let error = "";
		switch (system) {
			case "PRO":
				//	             if (this.projection) {
				//	                 let x = coordinate.px;
				//	                 let y = coordinate.py;
				//
				//	                 if (!this.isNumber(x) || !this.isNumber(y))
				//	                     return {
				//	                         error: "Values must be Numeric."
				//	                     };
				//
				//	                 let newVals = proj4(this.projection).inverse([x, y]);
				//	                 x = newVals[0];
				//	                 y = newVals[1];
				//
				//	                 if (x > 180 || x < -180)
				//	                     return {
				//	                         error: "Invalid X value used"
				//	                     };
				//
				//	                 if (y > 90 || y < -90)
				//	                     return {
				//	                         error: "Invalid Y value used"
				//	                     };
				//
				//	                 coordinate.x = x * 1;
				//	                 coordinate.y = y * 1;
				//	             } else {
				coordinate.x = coordinate.px * 1;
				coordinate.y = coordinate.py * 1;
				//	             }
				break;
			case "DDD":
				let x = coordinate.x;
				let y = coordinate.y;

				if (!this.isNumber(x) || !this.isNumber(y))
					return {
						error: "Values must be Numeric."
					};


				if (x > 180 || x < -180)
					return {
						error: "X must be between -180 & 180"
					};

				if (y > 90 || y < -90)
					return {
						error: "Y must be between -90 & 90"
					};

				latlon = this.DDD2DDD(x, y);
				if (!latlon) {
					return {
						error: "Please use valid decimal inputs."
					};
				}
				break;
			case "DMS":
				let xD = coordinate.xd;
				let xM = coordinate.xm;
				let xS = coordinate.xs;

				let yD = coordinate.yd;
				let yM = coordinate.ym;
				let yS = coordinate.ys;
				if (!this.isNumber(xD) || !this.isNumber(yD) || !this.isNumber(xS) || !this.isNumber(yS) || !this.isNumber(xM) || !this.isNumber(yM))
					return {
						error: "Values must be Numeric."
					};

				if (xD > 180 || xD < -180)
					return {
						error: "X Degrees must be between -180 & 180"
					};

				if (yD > 90 || yD < -90)
					return {
						error: "Y Degrees must be between -90 & 90"
					};

				if (yS > 59 || yM > 59 || xS > 59 || xM > 59 || yS < 0 || yM < 0 || xS < 0 || xM < 0)
					return {
						error: "X/Y Minutes & Seconds must be between 0 - 59"
					};

				let longitudeD = coordinate.xh == "East" ? 1 : -1
				let latitudeD = coordinate.yh == "North" ? 1 : -1

				latlon = this.DMS2DDD(xD, xM, xS,
					yD, yM, yS,
					longitudeD,
					latitudeD);
				if (!latlon) {
					return {
						error: "Please use valid Degree, Minute & Second inputs."
					};
				}
				break;
			case "UTM":
				let easting_utm = coordinate.easting;
				let northing_utm = coordinate.northing;
				let zone = coordinate.zone;


				if (!this.isNumber(easting_utm) || !this.isNumber(northing_utm) || !this.isNumber(zone))
					return {
						error: "All UTM inputs must be Numeric."
					};

				if (zone < 1 || zone > 59)
					return {
						error: "Zone must be between 1 - 59"
					};

				let isSouth = coordinate.utmHemisphere == "North" ? false : true
				latlon = this.UTM2DDD(northing_utm, easting_utm, zone, isSouth);
				if (!latlon) {
					return {
						error: "Failed. Please use valid UTM values."
					};
				}
				break;
			case "CCS":
				let nad83 = coordinate.ccsnad83;
				let easting = coordinate.easting;
				let northing = coordinate.northing;
				if (!this.isNumber(easting) || !this.isNumber(northing))
					return {
						error: "All CCS inputs must be Numeric."
					};
				latlon = this.CCS2DDD(northing, easting, nad83);
				if (!latlon)
					return {
						error: "Failed. Please use valid CCS values."
					};
				break;
			case "MGRS":
				let mgrsString = coordinate.point;

				latlon = mgrs.toPoint(mgrsString);
				if (latlon) {
					latlon = {
						x: latlon[0],
						y: latlon[1]
					};

				} else {
					return {
						error: "Please use valid MGRS values, e.g. 19TDJ3858897366"
					};
				}
				break;
		}

		if (latlon) {
			let utm = this.DDD2UTM(latlon.x * 1, latlon.y * 1);
			let dms = this.DDD2DMS(latlon.x * 1, latlon.y * 1);
			let ccs = coordinate.ccsnad83 ? this.DDD2CCS(latlon.x * 1, latlon.y * 1, coordinate.ccsnad83) : null

			if (system != "DDD") {
				coordinate.x = latlon.x;
				coordinate.y = latlon.y;
			}

			// if(system != "CCS"){
			//     coordinate.easting = ccs.easting;
			//     coordinate.northing = ccs.northing;
			// };

			if (system != "UTM") {
				coordinate.easting = utm.easting;
				coordinate.northing = utm.northing;
				coordinate.zone = utm.zone;
				coordinate.utmHemisphere = utm.orientation == "N" ? "North" : "South"
			}

			if (system != "DMS") {
				coordinate.xd = dms[0][0];
				coordinate.xm = dms[0][1];
				coordinate.xs = dms[0][2];
				coordinate.xh = dms[0][3] == "E" ? "East" : "West"

				coordinate.yd = dms[1][0];
				coordinate.ym = dms[1][1];
				coordinate.ys = dms[1][2];
				coordinate.yh = dms[1][3] == "N" ? "North" : "South"
			}

			if (system != "MGRS")
				coordinate.point = mgrs.forward([latlon.x, latlon.y], 5);

			return latlon;
		}
	}
}
